/**
 * BamCoachBadge — gold pill rendered next to internal BAM Coaches' names
 * across the platform.
 *
 * Distinct from CertBadge in scope and visual weight: this signals "this
 * person works for BAM Coaches" — it's the loudest signal so coaches can
 * recognize official staff in the community. Internal staff don't show
 * the cert badge separately (assumed certified).
 *
 * Driven by `profiles.is_bam_coach`. Toggled manually from the admin
 * portal — never auto-derived, since "is on staff" is an HR fact, not a
 * data-derivable signal.
 *
 * Two sizes:
 *   sm → fits inline next to a name (community feed, leaderboard)
 *   md → standalone in profile cards / hero banners
 *
 * Usage:
 *   <BamCoachBadge size="sm" />
 */
import React, { useState } from "react";
import { GOLD } from "../constants/theme";

export function BamCoachBadge({ size = "sm", tooltip = true, style }) {
  const [hover, setHover] = useState(false);

  const isMd = size === "md";

  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="BAM Coaches staff member"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: isMd ? 5 : 4,
        padding: isMd ? "3px 9px" : "1px 6px",
        borderRadius: 999,
        background: GOLD,
        color: "#111",
        fontSize: isMd ? 11 : 9.5,
        fontWeight: 800,
        fontFamily: "'Bebas Neue',sans-serif",
        letterSpacing: isMd ? 1.2 : 0.8,
        lineHeight: 1.3,
        flexShrink: 0,
        verticalAlign: "middle",
        cursor: tooltip ? "help" : "default",
        position: "relative",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {/* Star glyph — kept inline as a SVG so font fallback can't replace
          it with a different shape across browsers. */}
      <svg
        width={isMd ? 11 : 9}
        height={isMd ? 11 : 9}
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        <path
          d="M6 0.8 L7.45 4.6 L11.4 4.85 L8.35 7.4 L9.35 11.2 L6 8.95 L2.65 11.2 L3.65 7.4 L0.6 4.85 L4.55 4.6 Z"
          fill="#111"
        />
      </svg>
      BAM COACH

      {tooltip && hover && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            background: "#111",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "4px 8px",
            borderRadius: 5,
            whiteSpace: "nowrap",
            zIndex: 9999,
            letterSpacing: 0.2,
            fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            pointerEvents: "none",
          }}
        >
          Official BAM Coaches staff
        </span>
      )}
    </span>
  );
}

export default BamCoachBadge;
